import React, { ReactNode, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PriceTierWithParticipants } from 'components/Booking/PriceTier/types';
import { Flex } from 'components/common/styled/common-styled';
import { ResourceParagraph } from 'components/theme/Typography/ResourceTypography';
import Add from 'assets/plus-circle';
import Minus from 'assets/minus-circle';
import { currencyFormat } from 'utils';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import { useCalculateAdvertisedTierPrice } from 'utils/calculateAdvertisedTierPrice';
import type { ITax } from 'types/experience.types';

export interface StayPricing {
  label: string;
  priceByTierName: Record<string, number>;
}

interface NonMappedPriceTierSelectorProps {
  hideQuantityStepper?: boolean;
  priceTiers: PriceTierWithParticipants[];
  maxParticipants: number;
  minParticipants?: number;
  stayPricing?: StayPricing | null;
  taxes?: ITax[];
  renderTierDetails?: (tier: PriceTierWithParticipants) => ReactNode;
  onChange: (
    tierId: PriceTierWithParticipants['id'],
    increment: number,
  ) => void;
}

const NonMappedPriceTierSelector = ({
  hideQuantityStepper = false,
  priceTiers,
  maxParticipants,
  minParticipants = 0,
  stayPricing = null,
  taxes = [],
  renderTierDetails,
  onChange,
}: NonMappedPriceTierSelectorProps) => {
  const selectedParticipants = useMemo(() => {
    return priceTiers.reduce((sum, tier) => {
      return sum + tier.participants;
    }, 0);
  }, [priceTiers]);

  return (
    <Flex
      direction="column"
      gap={hideQuantityStepper ? 0 : 20}
      alignItem="flex-start"
      width="100%"
    >
      {priceTiers.map((tier) => {
        return (
          <TierBlock key={tier.id} $split={hideQuantityStepper}>
            <PriceTierSelector
              hideQuantityStepper={hideQuantityStepper}
              priceTier={tier}
              taxes={taxes}
              minParticipants={minParticipants}
              maxParticipants={
                maxParticipants - selectedParticipants + tier.participants
              }
              onChange={(increment) => onChange(tier.id, increment)}
              stayPricing={stayPricing}
            />
            {renderTierDetails?.(tier)}
          </TierBlock>
        );
      })}
    </Flex>
  );
};

// With variants the tier sits on the left and its variants on the right, each tier divided from the next.
const TierBlock = styled.div<{ $split: boolean }>`
  display: grid;
  gap: 16px;
  width: 100%;
  ${({ $split }) =>
    $split &&
    css`
      grid-template-columns: 1fr 1fr;
      align-items: start;
      padding: 24px 0;
      & + & {
        border-top: 0.5px solid var(--way-colors-borderColor);
      }
      @media screen and (max-width: 768px) {
        grid-template-columns: 1fr;
      }
    `}
`;

interface SelectorProps {
  hideQuantityStepper?: boolean;
  priceTier: PriceTierWithParticipants;
  minParticipants?: number;
  maxParticipants: number;
  stayPricing?: StayPricing | null;
  taxes?: ITax[];
  onChange: (increment: number) => void;
}

export const PriceTierSelector = ({
  hideQuantityStepper = false,
  priceTier,
  minParticipants = 0,
  maxParticipants,
  stayPricing = null,
  taxes = [],
  onChange,
}: SelectorProps) => {
  const currency = useBrandCurrency();
  const { t: translate } = useTranslation();
  const calculateAdvertisedTierPrice = useCalculateAdvertisedTierPrice();

  const isSoldOut =
    maxParticipants === 0 || maxParticipants <= priceTier.participants;
  const isAtMinimum = priceTier.participants === minParticipants;

  const onIncreaseParticipantNumber = (
    e: React.MouseEvent | React.KeyboardEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSoldOut) return;

    onChange(1);
  };

  const onDecreaseParticipantsNumber = (
    e: React.MouseEvent | React.KeyboardEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAtMinimum) return;

    onChange(-1);
  };

  const { t } = useTranslation();

  return (
    <Flex direction="row" alignItem="center" width="100%">
      <Flex direction="column" alignItem="flex-start">
        <ResourceParagraph
          className="resource-group__price-tier-name"
          fontSize="16px"
          fontWeight="700"
          style={{ userSelect: 'none' }}
        >
          {priceTier.name}
        </ResourceParagraph>
        {currency && (
          <ResourceParagraph
            className="resource-group__price-tier-amount"
            fontSize="14px"
            fontWeight="400"
            style={{ userSelect: 'none' }}
          >
            {describeTierPrice({
              advertised: (amount) =>
                currencyFormat(currency)(
                  calculateAdvertisedTierPrice(amount, taxes),
                ),
              complimentary: translate('complimentary'),
              priceTier,
              stayPricing,
            })}
          </ResourceParagraph>
        )}
      </Flex>
      {!hideQuantityStepper && (
        <Flex direction="row" alignItem="center" gap={12}>
          <MinusIcon
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) =>
              e.key === 'Enter' && onDecreaseParticipantsNumber(e)
            }
            aria-label={t('decreasePriceTierTickets', {
              priceTier: priceTier.name,
            })}
            data-testid={`remove-${priceTier.name}`}
            onClick={onDecreaseParticipantsNumber}
            disabled={isAtMinimum}
          />
          <ResourceParagraph
            data-testid={`resource-value-${priceTier.name}`}
            className="resource-group__price-tier-number"
            fontSize="16px"
            fontWeight="700"
            style={{ userSelect: 'none', minWidth: 20, textAlign: 'center' }}
          >
            {priceTier.participants}
          </ResourceParagraph>
          <PlusIcon
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) =>
              e.key === 'Enter' && onIncreaseParticipantNumber(e)
            }
            aria-label={t('increasePriceTierTickets', {
              priceTier: priceTier.name,
            })}
            data-testid={`add-${priceTier.name}`}
            onClick={onIncreaseParticipantNumber}
            disabled={isSoldOut}
          />
        </Flex>
      )}
    </Flex>
  );
};

const PlusIcon = styled(Add)<{ disabled: boolean }>`
  pointer-events: auto;
  color: ${({ disabled }) =>
    disabled
      ? 'var(--way-colors-contrastColorShades-70)'
      : 'var(--way-colors-buttonColorShades-100)'};
  cursor: ${({ disabled }) => (disabled && 'not-allowed') || 'pointer'};
  &:hover {
    opacity: 0.71;
  }
`;

const MinusIcon = styled(Minus)<{ disabled: boolean }>`
  pointer-events: auto;
  color: ${({ disabled }) =>
    disabled
      ? 'var(--way-colors-contrastColorShades-70)'
      : 'var(--way-colors-buttonColorShades-100)'};
  cursor: ${({ disabled }) => (disabled && 'not-allowed') || 'pointer'};
  &:hover {
    opacity: 0.71;
  }
`;

export default NonMappedPriceTierSelector;

/**
 * The tier's price line: complimentary when free, the stay's price "for 2 nights" once a stay is picked
 * and the server has priced it, else the tier's own rate.
 */
const describeTierPrice = ({
  advertised,
  complimentary,
  priceTier,
  stayPricing,
}: {
  advertised: (amount: number) => string;
  complimentary: string;
  priceTier: PriceTierWithParticipants;
  stayPricing: StayPricing | null;
}): string => {
  if (priceTier.price === 0) {
    return complimentary;
  }
  const stayPrice = stayPricing?.priceByTierName[priceTier.name];
  if (stayPrice === undefined) {
    return advertised(priceTier.price);
  }
  return `${advertised(stayPrice)} ${stayPricing?.label ?? ''}`.trim();
};
